/**
 * Several presentations of one screen (I-2510): an app shows the data of a
 * screen in more than one way — the classic page or a new one, a list or a
 * table — and the account config path decides which one an account sees.
 *
 * A screen comes in two parts:
 *
 * - its **contract**: what the screen offers — data, actions, rights and the
 *   lifecycle (sockets, shortcuts, route intents, dialogs every presentation
 *   needs). The route's component builds it once, with the app's composables,
 *   and hands it to the presentation as the prop `screen`.
 * - its **presentations**: components that only show the contract. They fetch
 *   no data of their own and own no route — route → contract → presentation —
 *   so a new presentation is pure UI work and switching keeps the state.
 *
 * Which presentation shows is a setting like any other: the app's key
 * `<app>.presentation` holds one top-level field per screen with the id of
 * its presentation (`{ "conversations": "focus" }`). It is chosen in the
 * settings list only — a tenant or company sets and locks it per screen, a
 * design can carry it, the user picks it there; no screen offers a switch of
 * its own. A missing or unknown id falls back to the screen's default
 * presentation.
 *
 * Pure functions, no Vue rendering: the host is in `./host.ts`, the state in
 * `./usePresentation.ts`.
 */
import type { Component } from 'vue';
import type { ConnectSettingsField } from '../config/settingsDeclaration';

/** One way to show a screen. */
export interface ConnectPresentation {
  /**
   * Stored in the config (`[a-z][a-z0-9_]*`): keep it once it shipped, a
   * renamed id falls back to the default for everybody who picked it.
   */
  id: string;
  /** i18n key of the caption in the app's table: the setting's option. */
  labelKey: string;
  /** An icon of the presentation, for a host that shows the choice with icons. */
  icon?: Component;
  /**
   * Gets the screen's contract as the prop `screen`. Load it lazily
   * (`defineAsyncComponent`), so an account only loads what it sees.
   */
  component: Component;
}

/** A screen of an app with its presentations. `C` is the contract its presentations get. */
export interface ConnectScreenDefinition<C = unknown> {
  /** The app's module key; the setting lives in `<app>.presentation`. */
  app: string;
  /** The screen's field in `<app>.presentation` (`[a-z][a-z0-9_]*`) — also the unit a lock holds. */
  id: string;
  /** i18n keys of the setting's title and of what it does, in the app's table. */
  labelKey: string;
  descriptionKey: string;
  /** What an account sees while no layer picks a presentation. */
  defaultPresentation: string;
  /** In the order the switch and the settings list show them. */
  presentations: readonly ConnectPresentation[];
  /** Carries the contract's type to the host; never set. */
  readonly contract?: C;
}

/** The last segment of every app's presentation key. */
export const CONNECT_PRESENTATION_KEY_SUFFIX = 'presentation';

const ID_PATTERN = /^[a-z][a-z0-9_]*$/;

/** A screen's definition, typed by the contract its presentations get. */
export function defineConnectScreen<C>(
  definition: Omit<ConnectScreenDefinition<C>, 'contract'>
): ConnectScreenDefinition<C> {
  return definition;
}

/** The config key that holds the presentations of an app's screens: `<app>.presentation`. */
export function connectPresentationConfigKey(app: string): string {
  return `${app}.${CONNECT_PRESENTATION_KEY_SUFFIX}`;
}

/**
 * The presentation a stored value names; the screen's default for a missing
 * or unknown one (and the first presentation should the default be unknown
 * too — `connectScreenProblems` reports that).
 */
export function resolveConnectPresentation(
  definition: ConnectScreenDefinition<unknown>,
  stored: unknown
): ConnectPresentation {
  const { presentations } = definition;
  const picked =
    typeof stored === 'string' ? presentations.find((p) => p.id === stored) : undefined;
  return (
    picked ??
    presentations.find((p) => p.id === definition.defaultPresentation) ??
    presentations[0]!
  );
}

/**
 * The screen's row in the app's settings declaration (`settingsSections`):
 * which presentation the screen shows. Its options are the presentations,
 * its default the screen's; one field per screen, so a tenant or company
 * locks each screen on its own.
 */
export function connectPresentationSettingsField(
  definition: ConnectScreenDefinition<unknown>,
  extra: Pick<ConnectSettingsField, 'group' | 'keywords' | 'advanced' | 'preview'> = {}
): ConnectSettingsField {
  return {
    key: connectPresentationConfigKey(definition.app),
    path: [definition.id],
    control: definition.presentations.length > 4 ? 'select' : 'choice',
    options: definition.presentations.map((p) => ({ value: p.id, labelKey: p.labelKey })),
    default: definition.defaultPresentation,
    labelKey: definition.labelKey,
    descriptionKey: definition.descriptionKey,
    ...extra,
  };
}

/** Everything wrong with a screen's definition, one line per problem (empty: fine). */
export function connectScreenProblems(definition: ConnectScreenDefinition<unknown>): string[] {
  const where = `screen ${definition.app}.${definition.id}`;
  const problems: string[] = [];
  if (!ID_PATTERN.test(definition.app)) problems.push(`${where}: ${definition.app} is no app key`);
  if (!ID_PATTERN.test(definition.id)) problems.push(`${where}: ${definition.id} is no screen id`);
  if (!definition.presentations.length) problems.push(`${where}: no presentations`);
  const ids = new Set<string>();
  for (const presentation of definition.presentations) {
    if (!ID_PATTERN.test(presentation.id))
      problems.push(`${where}: ${presentation.id} is no presentation id`);
    if (ids.has(presentation.id)) problems.push(`${where}: ${presentation.id} declared twice`);
    ids.add(presentation.id);
  }
  if (!ids.has(definition.defaultPresentation))
    problems.push(`${where}: the default ${definition.defaultPresentation} is no presentation`);
  return problems;
}
